import type { KeyboardEvent } from 'react'
import { Box } from '@mui/material'
import { useIntl } from 'react-intl'

import { tokens, shortScreen } from '@configuration/theme'

import ShieldIcon from '@icons/Shield.icon'
import UserIcon from '@icons/User.icon'
import LockIcon from '@icons/Lock.icon'

import type { SettingsTab } from '../../SettingsPage.utils'

interface Props {
  value: SettingsTab
  onChange: (next: SettingsTab) => void
}

// Shared id helpers so the tab buttons and their panels (rendered by the
// container) can cross-reference via aria-controls / aria-labelledby.
export const settingsTabId = (value: SettingsTab): string => `settings-tab-${value}`
export const settingsTabPanelId = (value: SettingsTab): string => `settings-tabpanel-${value}`

const TABS: { value: SettingsTab; labelKey: string; Icon: typeof UserIcon }[] = [
  { value: 'profile', labelKey: 'settings.tabs.profile', Icon: UserIcon },
  { value: 'password', labelKey: 'settings.tabs.password', Icon: LockIcon },
  { value: 'twoFactor', labelKey: 'settings.tabs.twoFactor', Icon: ShieldIcon },
]

// Underlined tab row from the design: muted resting state, black + violet
// underline when selected. Keeps the `value`/`onChange` contract and the three
// SettingsTab values so the container logic and e2e locators are unchanged.
// Implements the full WAI-ARIA tabs pattern: roving tabindex (one tab stop)
// plus Arrow/Home/End key navigation.
const SettingsTabs = ({ value, onChange }: Props) => {
  const intl = useIntl()

  const moveTo = (index: number): void => {
    const next = TABS[(index + TABS.length) % TABS.length]
    onChange(next.value)
    // Move focus to follow the selection (automatic-activation tabs pattern).
    document.getElementById(settingsTabId(next.value))?.focus()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number): void => {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault()
        moveTo(currentIndex + 1)
        break
      case 'ArrowLeft':
        event.preventDefault()
        moveTo(currentIndex - 1)
        break
      case 'Home':
        event.preventDefault()
        moveTo(0)
        break
      case 'End':
        event.preventDefault()
        moveTo(TABS.length - 1)
        break
      default:
        break
    }
  }

  return (
    <Box
      role='tablist'
      data-testid='settings-tabs'
      aria-label={intl.formatMessage({ id: 'settings.tabs.aria' })}
      sx={{ display: 'flex', gap: 3, borderBottom: `1px solid ${tokens.borderDefault}` }}
    >
      {TABS.map(({ value: tabValue, labelKey, Icon }, index) => {
        const selected = value === tabValue
        return (
          <Box
            key={tabValue}
            component='button'
            type='button'
            role='tab'
            id={settingsTabId(tabValue)}
            aria-selected={selected}
            aria-controls={settingsTabPanelId(tabValue)}
            // Roving tabindex: only the active tab is in the tab order.
            tabIndex={selected ? 0 : -1}
            data-testid={`settings-tab-${tabValue}`}
            onClick={() => onChange(tabValue)}
            onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => handleKeyDown(event, index)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              padding: '12px 0',
              [shortScreen.tight]: { padding: '8px 0' },
              background: 'none',
              border: 0,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: selected ? 500 : 400,
              color: selected ? tokens.businessBlack : tokens.businessBlack60,
              position: 'relative',
              transition: 'color .3s',
              '&:hover': { color: tokens.businessBlack },
              '&:focus-visible': {
                outline: `2px solid ${tokens.versatileViolet}`,
                outlineOffset: 2,
              },
              '&::after': selected
                ? {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: '-1px',
                    height: '2px',
                    background: tokens.versatileViolet,
                  }
                : undefined,
            }}
          >
            <Icon />
            {intl.formatMessage({ id: labelKey })}
          </Box>
        )
      })}
    </Box>
  )
}

export default SettingsTabs
